import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Edit3 } from 'lucide-react';
import trainingApi from '../api';
import { nativeLabelFor } from '../i18n/strings';

const TrainingsList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await trainingApi.adminListAll();
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      await trainingApi.adminCreate({ title: newTitle.trim() });
      setNewTitle('');
      setCreating(false);
      refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this training and all attempts? This cannot be undone.')) return;
    try {
      await trainingApi.adminDelete(id);
      refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Trainings</h1>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="px-3 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> New Training
        </button>
      </div>

      {creating && (
        <form onSubmit={handleCreate} className="mb-4 flex gap-2">
          <input
            autoFocus
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Training title"
            className="flex-1 px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100"
          />
          <button type="submit" className="px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-white">
            Create
          </button>
          <button
            type="button"
            onClick={() => { setCreating(false); setNewTitle(''); }}
            className="px-3 py-2 rounded bg-slate-700 hover:bg-slate-600"
          >
            Cancel
          </button>
        </form>
      )}

      {error && (
        <div className="mb-4 p-3 rounded bg-rose-900/40 border border-rose-700 text-rose-200">{error}</div>
      )}

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-400">No trainings yet. Create one to get started.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-800 text-slate-300">
              <tr>
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">Languages</th>
                <th className="text-left px-4 py-2">Slides</th>
                <th className="text-left px-4 py-2">Questions</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Updated</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} className="border-t border-slate-700 hover:bg-slate-800/60">
                  <td className="px-4 py-2 font-medium">{t.title}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(t.languages || []).length === 0 ? (
                        <span className="text-xs text-slate-500">—</span>
                      ) : (
                        (t.languages || []).map((l) => (
                          <span
                            key={l.language}
                            title={l.is_available
                              ? `${l.slide_count} slides · available to trainees`
                              : `${l.slide_count} slides · hidden from trainees`}
                            className={`px-1.5 py-0.5 text-xs rounded border ${
                              l.is_available
                                ? 'border-emerald-800 bg-emerald-900/30 text-emerald-300'
                                : 'border-slate-600 bg-slate-700/40 text-slate-400'
                            }`}
                          >
                            {nativeLabelFor(l.language)}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2">{t.slide_count}</td>
                  <td className="px-4 py-2">{t.question_count}</td>
                  <td className="px-4 py-2">
                    {t.is_published ? (
                      <span className="px-2 py-0.5 text-xs rounded bg-emerald-900/40 text-emerald-300">
                        Published
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs rounded bg-slate-700 text-slate-300">
                        Draft
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-400">
                    {new Date(t.updated_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to={`${t.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-amber-300 hover:text-amber-200"
                    >
                      <Edit3 className="w-4 h-4" /> Edit
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(t.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-rose-300 hover:text-rose-200 ml-2"
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TrainingsList;
